import { cardContext } from '@context/card-context';
import { editedCardFieldsContext } from '@context/edited-card-fields-context';
import { PopoverContext } from '@context/popover-context';
import useNode from '@hooks/use-node';
import useOnFocus from '@hooks/use-on-focus';
import useValidateInput, { notNullValidator } from '@hooks/use-validate-input';
import { Button, Stack, TextField } from '@ui-lib';
import { useContext, useEffect, useState } from 'react';

import LinkStatusContainer from '../LinkStatusContainer/LinkStatusContainer';
import styles from './NameEditPopover.module.scss';

const NameEditPopover: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [firstNameInputNode, firstNameInputRef] = useNode<HTMLInputElement>();
  const { cardFields, cardFieldsDispatch } = useContext(cardContext);
  const [isError, errorMessage] = useValidateInput(cardFields.firstName || '', [notNullValidator]);
  // Only surface the "required" error once the user has left the field, so the
  // auto-focused popover doesn't show an error before any interaction.
  const [, , firstNameBlurred] = useOnFocus(firstNameInputNode);
  const showFirstNameError = isError && firstNameBlurred;
  const { show, onPositionDepsChange } = useContext(PopoverContext);
  const { onEditAttribute, editedAttributes } = useContext(editedCardFieldsContext);
  const [showAllInputs, setShowAllInputs] = useState(false);

  useEffect(() => {
    if (!show) setShowAllInputs(false);
  }, [show]);

  useEffect(() => {
    if (show && firstNameInputNode) {
      const intersectionObserver = new IntersectionObserver((entries, observer) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setTimeout(() => {
            firstNameInputNode.focus();
          }, 150);
          observer.unobserve(entry.target);
        }
      });

      intersectionObserver.observe(firstNameInputNode);
    }
  }, [show, firstNameInputNode]);

  return (
    <Stack gap="large">
      <LinkStatusContainer linked={cardFields.linkedAttributes.includes('first_name')}>
        <TextField
          ref={firstNameInputRef}
          value={cardFields.firstName || ''}
          onChange={(event) => {
            onEditAttribute('first_name');
            cardFieldsDispatch({ type: 'UPDATE_FIRST_NAME', value: event.currentTarget.value });
          }}
          label="First name"
          autoComplete="given-name"
          message={showFirstNameError ? errorMessage : undefined}
          tone={showFirstNameError ? 'critical' : 'neutral'}
        />
      </LinkStatusContainer>
      {(showAllInputs || cardFields.middleName || editedAttributes.includes('middle_name')) && (
        <LinkStatusContainer linked={cardFields.linkedAttributes.includes('middle_name')}>
          <TextField
            value={cardFields.middleName || ''}
            onChange={(event) => {
              onEditAttribute('middle_name');
              cardFieldsDispatch({ type: 'UPDATE_MIDDLE_NAME', value: event.currentTarget.value });
            }}
            label="Middle name"
            autoComplete="additional-name"
          />
        </LinkStatusContainer>
      )}

      <LinkStatusContainer linked={cardFields.linkedAttributes.includes('last_name')}>
        <TextField
          value={cardFields.lastName || ''}
          onChange={(event) => {
            onEditAttribute('last_name');
            cardFieldsDispatch({ type: 'UPDATE_LAST_NAME', value: event.currentTarget.value });
          }}
          label="Last name"
          autoComplete="family-name"
        />
      </LinkStatusContainer>

      {(showAllInputs ||
        cardFields.honorificPrefix ||
        editedAttributes.includes('honorific_prefix')) && (
        <LinkStatusContainer linked={cardFields.linkedAttributes.includes('honorific_prefix')}>
          <TextField
            value={cardFields.honorificPrefix || ''}
            onChange={(event) => {
              onEditAttribute('honorific_prefix');
              cardFieldsDispatch({ type: 'UPDATE_PREFIX', value: event.currentTarget.value });
            }}
            label="Prefix"
            autoComplete="honorific-prefix"
          />
        </LinkStatusContainer>
      )}

      {(showAllInputs ||
        cardFields.honorificSuffix ||
        editedAttributes.includes('honorific_suffix')) && (
        <LinkStatusContainer linked={cardFields.linkedAttributes.includes('honorific_suffix')}>
          <TextField
            value={cardFields.honorificSuffix || ''}
            onChange={(event) => {
              onEditAttribute('honorific_suffix');
              cardFieldsDispatch({ type: 'UPDATE_SUFFIX', value: event.currentTarget.value });
            }}
            label="Suffix"
            autoComplete="honorific-suffix"
          />
        </LinkStatusContainer>
      )}

      {(showAllInputs || cardFields.maidenName || editedAttributes.includes('maiden_name')) && (
        <LinkStatusContainer linked={cardFields.linkedAttributes.includes('maiden_name')}>
          <TextField
            value={cardFields.maidenName || ''}
            onChange={(event) => {
              onEditAttribute('maiden_name');
              cardFieldsDispatch({ type: 'UPDATE_MAIDEN_NAME', value: event.currentTarget.value });
            }}
            label="Maiden Name"
          />
        </LinkStatusContainer>
      )}

      {(showAllInputs ||
        cardFields.preferredName ||
        editedAttributes.includes('preferred_name')) && (
        <LinkStatusContainer linked={cardFields.linkedAttributes.includes('preferred_name')}>
          <TextField
            value={cardFields.preferredName || ''}
            onChange={(event) => {
              onEditAttribute('preferred_name');
              cardFieldsDispatch({
                type: 'UPDATE_PREFERRED_NAME',
                value: event.currentTarget.value,
              });
            }}
            label="Preferred Name"
            autoComplete="nickname"
          />
        </LinkStatusContainer>
      )}

      {(showAllInputs || cardFields.pronouns || editedAttributes.includes('pronouns')) && (
        <LinkStatusContainer linked={cardFields.linkedAttributes.includes('pronouns')}>
          <TextField
            value={cardFields.pronouns || ''}
            onChange={(event) => {
              onEditAttribute('pronouns');
              cardFieldsDispatch({ type: 'UPDATE_PRONOUNS', value: event.currentTarget.value });
            }}
            label="Pronouns"
          />
        </LinkStatusContainer>
      )}

      {!showAllInputs && (
        <div className={styles['show-all-btn-container']}>
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setShowAllInputs(true);
              onPositionDepsChange();
            }}
          >
            + Show more
          </Button>
        </div>
      )}
    </Stack>
  );
};

export default NameEditPopover;
